import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const TOOLBAR = [
  ['bold', 'italic', 'underline'],
  [{ header: [1, 2, 3, false] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean'],
];

const RichTextEditor = forwardRef(function RichTextEditor({
  initialContent,
  onChange,
  placeholder = 'Type the next part of the service here…',
  fontSize = 18,
  ariaLabel = 'Sermon block editor',
  onEnter,
}, ref) {
  const containerRef = useRef(null);
  const quillRef = useRef(null);
  const initialContentRef = useRef(initialContent);
  const onChangeRef = useRef(onChange);
  const onEnterRef = useRef(onEnter);
  onChangeRef.current = onChange;
  onEnterRef.current = onEnter;

  useImperativeHandle(ref, () => ({
    getContents: () => quillRef.current?.getContents() || { ops: [{ insert: '\n' }] },
    getText: () => quillRef.current?.getText() || '',
    setContents: (content) => quillRef.current?.setContents(content || { ops: [{ insert: '\n' }] }),
    focus: () => quillRef.current?.focus(),
  }), []);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const container = containerRef.current;
    const quill = new Quill(container, {
      theme: 'snow',
      modules: { toolbar: TOOLBAR },
      placeholder,
    });
    if (initialContentRef.current) quill.setContents(initialContentRef.current);
    quill.root.setAttribute('aria-label', ariaLabel);
    const handleKeyDown = (event) => {
      if (event.key !== 'Enter' || !onEnterRef.current?.()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    quill.root.addEventListener('keydown', handleKeyDown, true);
    quill.on('text-change', () => onChangeRef.current?.(quill.getContents()));
    quillRef.current = quill;
    return () => {
      quill.off('text-change');
      quill.root.removeEventListener('keydown', handleKeyDown, true);
      quillRef.current = null;
      container.innerHTML = '';
    };
  }, [ariaLabel, placeholder]);

  return (
    <div className="rich-editor" style={{ '--editor-text-size': `${fontSize}px` }}>
      <div ref={containerRef} className="rich-editor-container" />
    </div>
  );
});

export default RichTextEditor;
